/**
 * UserPreferencesProvider — Core Frontend wrapper (HBF-3)
 *
 * The actual sync logic lives in `@teletienda/shared-ui-lib`. This wrapper
 * injects core-frontend-specific data sources:
 *   - Profile from TanStack Query (`['userProfile']`)
 *   - Save callback that calls `coreAPI_Service.updateProfile` and invalidates
 *     the cached profile.
 *
 * Existing imports from `@/providers/UserPreferencesProvider` keep working
 * unchanged — `UserPreferencesProvider` and `useUserPreferences` are re-exported.
 */
import { useCallback, type ReactNode } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuth } from '@teletienda/shared-auth-ui';
import {
  UserPreferencesProvider as SharedUserPreferencesProvider,
  useUserPreferences as sharedUseUserPreferences,
} from '@teletienda/shared-ui-lib';

import { coreAPI_Service } from '@/lib/api/core';

interface UserPreferencesProviderProps {
  children: ReactNode;
}

export function UserPreferencesProvider({ children }: UserPreferencesProviderProps) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: () => coreAPI_Service.getMyProfile(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });

  const onSaveTheme = useCallback(
    async (newTheme: 'light' | 'dark') => {
      const updated = { ...profile?.ui_preferences, theme: newTheme };
      await coreAPI_Service.updateProfile({ preferences: { ui: updated } } as never);
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    },
    [profile?.ui_preferences, queryClient]
  );

  return (
    <SharedUserPreferencesProvider
      profile={profile ?? null}
      isAuthenticated={isAuthenticated}
      onSaveTheme={onSaveTheme}
    >
      {children}
    </SharedUserPreferencesProvider>
  );
}

export const useUserPreferences = sharedUseUserPreferences;
